import { Flex, Spin, Typography } from 'antd';
import { useEffect, useState } from 'react';

/** Long enough that a quick answer never flashes a spinner, short enough to feel immediate. */
const LOADING_MESSAGE_DELAY_MS = 300;

interface Props {
  /** What is being waited for, such as "Checking the API…". */
  readonly children: string;
  /** How long to wait before showing; 0 where the caller has waited already. */
  readonly delayMs?: number;
}

/**
 * The app's one loading indicator (D-073): a small spinner beside what is being waited
 * for. `SectionBoundary` shows it while a section's data loads, `PageLoading` while a
 * page's code does.
 */
export function LoadingMessage({ children, delayMs = LOADING_MESSAGE_DELAY_MS }: Props) {
  const [shown, setShown] = useState(delayMs === 0);

  useEffect(() => {
    if (delayMs === 0) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setShown(true);
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
  }, [delayMs]);

  return (
    // Hidden rather than absent while waiting, so the layout doesn't move when it shows.
    <Flex align="center" gap="small" style={shown ? undefined : { visibility: 'hidden' }}>
      <Spin size="small" />
      <Typography.Text>{children}</Typography.Text>
    </Flex>
  );
}
